// 全中台同一套优先级语言（月历待办、焦点、任务看板、列表都用这两个组件）：
//   优先级图标：紧急 = 红色方块带叹号；高 / 中 / 低 = 三格柱点亮 3 / 2 / 1 格；未定 = 三道短横
//   勾选圆圈：外圈颜色 = 优先级；里面 = 状态（空 = 待办，半满 = 进行中 / 待验收，实心带勾 = 已完成，斜线 = 已取消，虚线圈 = 待分拣）
import type { MouseEvent } from 'react'
import './ui.css'

/** 优先级：0 未定、1 紧急、2 高、3 中、4 低（和任务接口一致） */
export type Priority = 0 | 1 | 2 | 3 | 4
export type TaskStatus = 'triage' | 'todo' | 'in_progress' | 'in_review' | 'done' | 'canceled'

export const PRIORITY_NAMES = ['未定', '紧急', '高', '中', '低'] as const
const LEVEL = ['none', 'urgent', 'high', 'medium', 'low'] as const
/** 三格柱各点亮几格 */
const LIT = [0, 3, 3, 2, 1]

export function PriorityIcon({ priority, label = true }: { priority: number; label?: boolean }) {
  const p = (priority >= 0 && priority <= 4 ? priority : 0) as Priority
  const a11y = label ? { role: 'img', 'aria-label': `优先级：${PRIORITY_NAMES[p]}` } : { 'aria-hidden': true }
  if (p === 1) {
    return (
      <svg className="pri-icon" data-level="urgent" viewBox="0 0 16 16" {...a11y}>
        <rect className="pri-urgent" x="1" y="1" width="14" height="14" rx="3.5" />
        <rect className="pri-urgent-fg" x="7.1" y="4" width="1.8" height="5.2" rx="0.9" />
        <rect className="pri-urgent-fg" x="7.1" y="10.4" width="1.8" height="1.8" rx="0.9" />
      </svg>
    )
  }
  if (p === 0) {
    return (
      <svg className="pri-icon" data-level="none" viewBox="0 0 16 16" {...a11y}>
        {[1.5, 6.5, 11.5].map((x) => <rect key={x} className="pri-off" x={x} y="7.2" width="3" height="1.6" rx="0.8" />)}
      </svg>
    )
  }
  return (
    <svg className="pri-icon" data-level={LEVEL[p]} viewBox="0 0 16 16" {...a11y}>
      {[[1.5, 8, 6], [6.5, 5, 9], [11.5, 2, 12]].map(([x, y, h], i) => (
        <rect key={x} className={i < LIT[p] ? 'pri-on' : 'pri-off'} x={x} y={y} width="3" height={h} rx="1" />
      ))}
    </svg>
  )
}

const STATE: Record<TaskStatus, 'todo' | 'doing' | 'done' | 'canceled' | 'triage'> = {
  triage: 'triage',
  todo: 'todo',
  in_progress: 'doing',
  in_review: 'doing',
  done: 'done',
  canceled: 'canceled',
}
const STATUS_NAMES: Record<TaskStatus, string> = { triage: '待分拣', todo: '待办', in_progress: '进行中', in_review: '待验收', done: '已完成', canceled: '已取消' }

/**
 * 勾选圆圈。给了 onToggle 就是按钮（点一下完成 / 再点一下取消），没给就只是状态标记。
 * size：sm 用在卡片、列表的元信息里，md 用在待办行，lg 用在焦点卡片。
 */
export function TaskCheck({
  priority,
  status,
  title,
  size = 'md',
  onToggle,
}: {
  priority: number
  status: TaskStatus
  title?: string
  size?: 'sm' | 'md' | 'lg'
  onToggle?: (e: MouseEvent<HTMLButtonElement>) => void
}) {
  const p = (priority >= 0 && priority <= 4 ? priority : 0) as Priority
  const state = STATE[status] ?? 'todo'
  const common = { className: 'task-chk', 'data-level': LEVEL[p], 'data-state': state, 'data-size': size }
  const mark = (
    <>
      {state === 'done' && (
        <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.6 6.2l2.2 2.2 4.6-4.8" /></svg>
      )}
    </>
  )
  if (!onToggle) {
    return <span {...common} role="img" aria-label={STATUS_NAMES[status]} title={STATUS_NAMES[status]}>{mark}</span>
  }
  const done = state === 'done'
  return (
    <button
      type="button"
      {...common}
      aria-pressed={done}
      aria-label={`${done ? '取消完成' : '完成'}${title ? `「${title}」` : ''}`}
      title={STATUS_NAMES[status]}
      onClick={(e) => {
        e.stopPropagation()
        onToggle(e)
      }}
    >
      {mark}
    </button>
  )
}
